import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

import Dashboard from '@/views/Dashboard.vue'
import Login from '@/views/Login.vue'
import Profile from '@/views/Profile.vue'
import SeasonsManagement from '@/views/SeasonsManagement.vue'
import PrivacyPolicy from '@/views/PrivacyPolicy.vue'
import TermsOfService from '@/views/TermsOfService.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: () => import('@/views/Home.vue'),
    },
    {
      path: '/demo',
      name: 'Demo',
      component: () => import('@/views/Demo.vue'),
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
    },
    {
      path: '/auth/callback',
      name: 'AuthCallback',
      component: () => import('@/views/AuthCallback.vue'),
    },
    {
      path: '/invite/:token',
      name: 'InvitePage',
      component: () => import('@/views/InvitePage.vue'),
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: { requiresAuth: true },
    },
    {
      path: '/teams/:id',
      name: 'TeamView',
      component: () => import('@/views/TeamView.vue'),
      meta: { requiresAuth: true, requiresTeamView: true },
    },
    {
      path: '/teams/:id/manage',
      name: 'TeamManage',
      component: () => import('@/views/TeamManagement.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/teams/:id/games/new',
      name: 'GameCreate',
      component: () => import('@/views/GameForm.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/teams/:id/games/:gid/edit',
      name: 'GameEdit',
      component: () => import('@/views/GameForm.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/teams/:id/games/:gid/stats',
      name: 'GameStatsEntry',
      component: () => import('@/views/GameStatsEntry.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/teams/:id/games/:gid/lineup',
      name: 'LineupBuilder',
      component: () => import('@/views/LineupBuilder.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/teams/:id/invites',
      name: 'InviteManagement',
      component: () => import('@/views/InviteManagement.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/teams/:id/emails',
      name: 'CoachEmails',
      component: () => import('@/views/CoachEmails.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/teams/:id/opponents',
      name: 'Opponents',
      component: () => import('@/views/Opponents.vue'),
      meta: { requiresAuth: true, requiresTeamManage: true },
    },
    {
      path: '/my-stats',
      name: 'MyStats',
      component: () => import('@/views/MyStats.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/stats',
      name: 'Stats',
      component: () => import('@/views/PlayerStats.vue'),
      meta: { requiresAuth: true, requiresCoach: true },
    },
    {
      path: '/profile',
      name: 'Profile',
      component: Profile,
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/users',
      name: 'AdminUsers',
      component: () => import('@/views/AdminUserManagement.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/seasons',
      name: 'AdminSeasons',
      component: SeasonsManagement,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/venues',
      name: 'AdminVenues',
      component: () => import('@/views/VenueManagement.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/teams',
      name: 'AdminTeams',
      component: () => import('@/views/CreateTeams.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: PrivacyPolicy,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfService',
      component: TermsOfService,
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: () => {
        const authStore = useAuthStore()
        return authStore.isAuthenticated ? authStore.homeRoute : '/'
      },
    },
  ],
})

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next('/dashboard')
    return
  }
  if (to.meta.requiresCoach && !(authStore.isCoach || authStore.isAdmin)) {
    next('/dashboard')
    return
  }
  if (to.meta.requiresTeamManage && !authStore.canManageTeam(Number(to.params.id))) {
    next('/dashboard')
    return
  }
  if (to.meta.requiresTeamView && !authStore.canViewTeam(Number(to.params.id))) {
    next('/dashboard')
    return
  }
  if ((to.name === 'Login' || to.name === 'Home') && authStore.isAuthenticated) {
    next(authStore.homeRoute)
    return
  }

  next()
})

// Self-heal stale deployments: after a release, an already-open tab may try
// to lazy-load a route chunk whose hashed filename no longer exists. Instead
// of the click silently doing nothing, reload the app at the target route.
router.onError((error, to) => {
  const msg = String(error?.message ?? error)
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  ) {
    window.location.href = to?.fullPath ?? '/'
  }
})

export default router
